import React, { PropsWithChildren, useContext, useEffect, useState } from 'react'
import { AppSidebar } from '@/components/AppSidebar'
import { TourGuide } from '@/components/TourGuide/TourGuide'
import { HubConnectionState } from '@microsoft/signalr'
import { Layout } from 'antd'
import { useAppDispatch, useAppSelector } from 'store'
import { AuthSessionContext } from 'utils/AuthContext'
import { useUIVersion } from 'utils/UIVersionContext'

import { NotificationMessage, NotificationType } from './api/SignalR/Types'
import { TokenStorage } from './api/tokenStorage'
import { Nav } from './Nav'
import { SetTicketsAssignedToUserSession } from './store/tickets/tickets.actions'
import { WebSocketContext } from './utils/WebSocketContext'

import styles from './MainLayout.module.scss'

export const MainLayout: React.FC<PropsWithChildren> = (props) => {
  const [status, setStatus] = useState<HubConnectionState>(HubConnectionState.Disconnected)
  const [auth] = useContext(AuthSessionContext)
  const { hub } = useContext(WebSocketContext)

  const sessionId = TokenStorage.getSession()

  const dispatch = useAppDispatch()
  const permissions = auth?.permissions || []
  const outstandingTickets = useAppSelector((state) => state.tickets.outstandingTickets)

  const onNotification = (msg: NotificationMessage) => {
    if (msg.type === NotificationType.OUTSTANDING_TICKETS)
      dispatch(SetTicketsAssignedToUserSession(msg.data))
  }

  const onDisconnected = (_arg: Error | undefined) => {
    hub?.setStatus(HubConnectionState.Disconnected)
    setStatus(HubConnectionState.Disconnected)
  }

  useEffect(() => {
    ;(async () => {
      if (hub && hub?.getStatus() === HubConnectionState.Disconnected && auth && sessionId) {
        await hub.client.start()
        await hub.client.invoke('initializeAsync', auth?.user.id, sessionId)
        hub.client.on('notification', onNotification)
        hub.client.onclose((err) => onDisconnected(err))
        hub.setStatus(HubConnectionState.Connected)
        setStatus(HubConnectionState.Connected)
      }
    })()
  }, [hub, auth, sessionId])

  if (!auth) return props.children

  const { isNewUI, menuPosition } = useUIVersion()

  const isPopupWindow = window.opener !== null

  if (isNewUI) {
    if (isPopupWindow) {
      return (
        <main className='h-screen w-full overflow-y-auto bg-background p-6'>{props.children}</main>
      )
    }

    return (
      <div
        className={
          menuPosition === 'top'
            ? 'flex h-screen w-full flex-col overflow-hidden'
            : 'flex h-screen w-full overflow-hidden'
        }
      >
        {menuPosition !== 'right' && <AppSidebar />}
        <main className='min-h-0 min-w-0 flex-1 overflow-y-auto bg-background p-6'>
          {props.children}
        </main>
        {menuPosition === 'right' && <AppSidebar />}
      </div>
    )
  } else {
    if (isPopupWindow) {
      return (
        <div className={styles.main} style={{ minHeight: '100%', background: '#fff' }}>
          {props.children}
        </div>
      )
    }
  }

  // old UI
  return (
    <>
      <TourGuide />
      <Layout
        style={{
          height: '100%',
          overflow: 'hidden',
        }}
      >
        <Nav {...props} permissions={permissions} tickets={outstandingTickets} status={status} />
        <Layout style={{ overflowY: 'scroll' }}>
          <Layout.Content>
            <div className={styles.main} style={{ minHeight: '100%', background: '#fff' }}>
              {props.children}
            </div>
          </Layout.Content>
        </Layout>
      </Layout>
    </>
  )
}
